/**
 * Mapbox `geometry-type` matchers for MVT / GeoJSON style filters.
 *
 * Vector tiles often emit Multi* types; matching only LineString/Polygon
 * leaves those features invisible and unclickable.
 */

export const MAPBOX_POINT_GEOMETRY_FILTER = [
	"any",
	["==", ["geometry-type"], "Point"],
	["==", ["geometry-type"], "MultiPoint"],
] as const;

export const MAPBOX_POLYGON_GEOMETRY_FILTER = [
	"any",
	["==", ["geometry-type"], "Polygon"],
	["==", ["geometry-type"], "MultiPolygon"],
] as const;

/** Line outlines + polygon strokes (same as MapShell shape line layer). */
export const MAPBOX_LINE_OR_POLYGON_GEOMETRY_FILTER = [
	"any",
	["==", ["geometry-type"], "LineString"],
	["==", ["geometry-type"], "MultiLineString"],
	["==", ["geometry-type"], "Polygon"],
	["==", ["geometry-type"], "MultiPolygon"],
] as const;

const LINE_TYPES = new Set(["LineString", "MultiLineString"]);
const POLYGON_TYPES = new Set(["Polygon", "MultiPolygon"]);

interface GeometryLike {
	type?: unknown;
	coordinates?: unknown;
	geometry?: unknown;
}

/**
 * Unwraps GeoJSON Feature wrappers (`{ type: "Feature", geometry: ... }`) and
 * returns the effective geometry object used for shape classification.
 */
export function unwrapFeatureGeometry(
	geometry: unknown,
): { type: string; coordinates?: unknown } | null {
	if (geometry == null || typeof geometry !== "object") return null;
	const value = geometry as GeometryLike;
	if (typeof value.type !== "string") return null;
	if (
		value.type === "Feature" &&
		value.geometry != null &&
		typeof value.geometry === "object"
	) {
		return unwrapFeatureGeometry(value.geometry);
	}
	return { type: value.type, coordinates: value.coordinates };
}

/** Geometry type resolved after unwrapping Feature wrappers. */
export function getEffectiveGeometryType(
	geometry: unknown,
): string | undefined {
	return unwrapFeatureGeometry(geometry)?.type;
}

/** True for GeoJSON / Mapbox line geometry types (including Multi*). */
export function isLineGeometryType(type: string | undefined | null): boolean {
	return type != null && LINE_TYPES.has(type);
}

/** True for GeoJSON / Mapbox polygon geometry types (including Multi*). */
export function isPolygonGeometryType(
	type: string | undefined | null,
): boolean {
	return type != null && POLYGON_TYPES.has(type);
}

/** True for line or polygon (shape) geometry — not point/cluster. */
export function isShapeGeometryType(type: string | undefined | null): boolean {
	return isLineGeometryType(type) || isPolygonGeometryType(type);
}
